import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Button from './Button';
import ToggleSwitch from './ToggleSwitch';
import DropdownButton from './DropdownButton';
import CloseIcon from '../icons/CloseIcon';

interface Achievement {
  id?: string;
  title: string;
  description: string;
  imageUrl?: string;
  type: 'point' | 'firstCome' | 'ranking' | 'manual';
  status: 'inProgress' | 'completed';
  isActive: boolean;
  /** true면 학생에게 달성 전까지 이름·설명·조건 숨김 (트로피 시크릿) */
  isSecret?: boolean;
  targetValue?: number;
  createdAt?: Date;
  updatedAt?: Date;
}

interface AddNewAchievementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (achievement: Omit<Achievement, 'id' | 'createdAt' | 'updatedAt'>) => void;
  initialData?: Achievement;
  mode?: 'create' | 'edit';
}

const AddNewAchievementDialog: React.FC<AddNewAchievementDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  mode = 'create'
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [type, setType] = useState<'point' | 'firstCome' | 'ranking' | 'manual'>('point');
  const [targetValue, setTargetValue] = useState<number>(0);
  const [isActive, setIsActive] = useState(true);
  const [isSecret, setIsSecret] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setTitle(initialData.title || '');
        setDescription(initialData.description || '');
        setImageUrl(initialData.imageUrl || '');
        setType(initialData.type || 'point');
        setTargetValue(initialData.targetValue || 0);
        setIsActive(initialData.isActive ?? true);
        setIsSecret(initialData.isSecret ?? false);
      } else {
        setTitle('');
        setDescription('');
        setImageUrl('');
        setType('point');
        setTargetValue(0);
        setIsActive(true);
        setIsSecret(false);
      }
    }
  }, [isOpen, initialData]);

  const handleSubmit = () => {
    if (!title.trim() || !description.trim()) {
      alert('업적명과 설명을 입력해주세요.');
      return;
    }

    onSave({
      title: title.trim(),
      description: description.trim(),
      imageUrl: imageUrl.trim(),
      type,
      status: 'inProgress',
      isActive,
      isSecret,
      targetValue: type === 'manual' ? undefined : targetValue
    });
  };

  const typeOptions = [
    { value: 'point', label: '포인트 달성 (자동)' },
    { value: 'firstCome', label: '선착순 (자동)' },
    { value: 'ranking', label: '순위 (자동)' },
    { value: 'manual', label: '교사 직접 부여 (일반 업적)' }
  ];

  if (!isOpen) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '450px',
          background: 'var(--Color-background-normal, white)',
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)',
          borderRadius: '16px',
          overflow: 'hidden',
          position: 'relative',
          padding: '24px 28px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          minHeight: '672px',
          boxSizing: 'border-box'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '40px'
        }}>
            {/* 헤더 */}
            <div style={{
              alignSelf: 'stretch',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '8px'
            }}>
              <div style={{
                flex: 1,
                color: 'var(--Color-text-normal, #161616)',
                fontSize: '20px',
                fontFamily: 'Pretendard',
                fontWeight: '600',
                lineHeight: '30px'
              }}>
                {mode === 'edit' ? '업적 수정' : '새 업적 만들기'}
              </div>
              <div
                onClick={onClose}
                style={{
                  padding: '4px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#F5F5F5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <CloseIcon width={24} height={24} color="#0D0D0D" />
              </div>
            </div>

            {/* 폼 내용 */}
            <div style={{
              alignSelf: 'stretch',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px'
            }}>
          {/* 업적명 및 설명 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="업적명을 입력해 주세요"
              style={{
                width: '100%',
                height: '48px',
                padding: '12px 16px',
                background: 'var(--Color-fill-white, white)',
                borderRadius: '8px',
                border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                fontSize: '13px',
                fontFamily: 'Pretendard',
                fontWeight: '400',
                lineHeight: '19.5px',
                color: title ? '#161616' : 'var(--Color-text-assistive, #989898)',
                outline: 'none'
              }}
            />
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="업적에 대한 설명을 입력해 주세요"
              rows={3}
              style={{
                width: '100%',
                height: '84px',
                padding: '12px 16px',
                background: 'var(--Color-fill-white, white)',
                borderRadius: '8px',
                border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                fontSize: '13px',
                fontFamily: 'Pretendard',
                fontWeight: '400',
                lineHeight: '19.5px',
                color: description ? '#161616' : 'var(--Color-text-assistive, #989898)',
                outline: 'none',
                resize: 'none'
              }}
            />
          </div>

          {/* 업적 종류 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{
              color: 'var(--Color-text-normal, #161616)',
              fontSize: '16px',
              fontFamily: 'Pretendard',
              fontWeight: '600',
              lineHeight: '24px'
            }}>
              업적 종류
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
              <DropdownButton
                options={typeOptions}
                value={type}
                onChange={(value) => setType(value as 'point' | 'firstCome' | 'ranking' | 'manual')}
                width={type === 'manual' ? '100%' : '144px'}
                height="48px"
                size="small"
              />
              {type !== 'manual' && (
              <input
                type="number"
                value={targetValue}
                onChange={(e) => setTargetValue(parseInt(e.target.value, 10) || 0)}
                placeholder="목표 값"
                style={{
                  width: '242px',
                  height: '48px',
                  padding: '12px 16px',
                  background: 'var(--Color-fill-white, white)',
                  borderRadius: '8px',
                  border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                  fontSize: '13px',
                  fontFamily: 'Pretendard',
                  fontWeight: '400',
                  lineHeight: '19.5px',
                  color: '#161616',
                  outline: 'none'
                }}
              />
              )}
            </div>
            {type === 'manual' && (
              <div style={{
                fontSize: '12px',
                fontFamily: 'Pretendard',
                fontWeight: '400',
                lineHeight: '18px',
                color: 'var(--Color-text-assistive, #989898)'
              }}>
                자동 조건 없이 업적만 등록합니다. 저장 후 목록에서 학생을 선택해 부여할 수 있습니다.
              </div>
            )}
          </div>

          {/* 업적 이미지 업로드 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{
              color: 'var(--Color-text-normal, #161616)',
              fontSize: '16px',
              fontFamily: 'Pretendard',
              fontWeight: '600',
              lineHeight: '24px'
            }}>
              업적 이미지 업로드
            </div>
            <input
              type="text"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="링크를 입력해 주세요"
              style={{
                width: '100%',
                height: '48px',
                padding: '12px 16px',
                background: 'var(--Color-fill-white, white)',
                borderRadius: '8px',
                border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
                fontSize: '13px',
                fontFamily: 'Pretendard',
                fontWeight: '400',
                lineHeight: '19.5px',
                color: imageUrl ? '#161616' : 'var(--Color-text-assistive, #989898)',
                outline: 'none'
              }}
            />
          </div>

          {/* 업적 활성화 여부 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              color: 'var(--Color-text-normal, #161616)',
              fontSize: '16px',
              fontFamily: 'Pretendard',
              fontWeight: '600',
              lineHeight: '24px'
            }}>
              업적 활성화 여부
            </div>
            <ToggleSwitch
              checked={isActive}
              onChange={setIsActive}
              size="medium"
            />
          </div>

          {/* 비공개(시크릿) 업적 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                color: 'var(--Color-text-normal, #161616)',
                fontSize: '16px',
                fontFamily: 'Pretendard',
                fontWeight: '600',
                lineHeight: '24px'
              }}>
                비공개(시크릿) 업적
              </div>
              <ToggleSwitch
                checked={isSecret}
                onChange={setIsSecret}
                size="medium"
              />
            </div>
            <div style={{
              fontSize: '12px',
              fontFamily: 'Pretendard',
              fontWeight: '400',
              lineHeight: '18px',
              color: 'var(--Color-text-assistive, #989898)'
            }}>
              달성하기 전까지 학생에게는 이름·설명·달성 조건이 보이지 않습니다. 달성 후에는 트로피처럼 공개됩니다.
            </div>
          </div>
          </div>
        </div>

        {/* 하단 버튼 */}
        <div style={{
          width: '100%',
          display: 'flex',
          gap: '8px',
          justifyContent: 'flex-start',
          alignItems: 'center'
        }}>
          <Button
            width="124px"
            height="48px"
            variant="cancel"
            onClick={onClose}
          >
            취소
          </Button>
          <Button
            width="262px"
            height="48px"
            variant="submit"
            onClick={handleSubmit}
          >
            {mode === 'edit' ? '수정' : '저장'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddNewAchievementDialog;

